import { Calendar } from '../../models/map/Calendar'
import { Travel } from '../../models/travel/Travel'
import {
  DATA_FETCHING,
  LOAD_FLAG_SETTING,
  TRAVEL_FETCHING,
  STATE_CLEARING,
  TRAVEL_FLAG_LOADING,
  CALENDAR_FETCHING, CALENDAR_FLAG_LOADING, CRITICAL_EVENT_STATE_UPDATING, USER_FEEDBACK_UPDATING, TRAVEL_SELECTION
} from '../mutations'

import { getField, updateField } from 'vuex-map-fields'
import {
  CLEAR_MODULE_STATE,
  FETCH_CALENDAR,
  FETCH_DATA,
  FETCH_TRAVEL,
  SEND_USER_FEEDBACK,
  UPDATE_CRITICAL_EVENT_STATE, USER_STATISTICS_UPDATE
} from '../actions'
import $axios from '../../backend'
import { CalendarTravelEntry } from '../../models/map/CalendarTravelEntry'
import { MapSearch, UserCriticalEventConfirmation } from '../../enum'
import { UserFeedback } from '../../models/travel/UserFeedback'
import { dateToCalendarEntry } from '../../utils/calendar'
import { User } from '../../models/user/User'
import $vuetify from '../../plugins/vuetify'
import { USER_STATISTICS_MODULE } from '../modules'
import { UserStatistics } from '../../models/user/UserStatistics'

const getDefaultTravelState = (state = MapSearch.CALENDAR) => ({
  calendar: new Calendar(),
  travel: new Travel(),
  currentDate: '',
  currentUser: new User(),
  currentCalendarTravel: new CalendarTravelEntry(),
  driversStatistics: {},
  isTravelLoaded: state !== MapSearch.CALENDAR,
  isCalendarLoaded: state !== MapSearch.CALENDAR,
  isTravelSelected: false
})

const getDefaultState = () => {
  return {
    // each selection will have separate states
    travelData: {
      [MapSearch.CALENDAR]: { ...getDefaultTravelState() },
      [MapSearch.USER]: { ...getDefaultTravelState(MapSearch.USER) }
    },

    // travel selection
    selection: MapSearch.CALENDAR,
    activeSearchTab: MapSearch.CALENDAR, // need to change it, if we decide to make user search first

    snackbar: {
      active: false,
      text: ''
    },

    // flag-loaders
    isLoaded: false

  }
}

const getTravelData = (state) => state.travelData[state.selection]

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    updateField,
    [DATA_FETCHING] (state, payload) {
      // calendar loading
      let travelData = getTravelData(state)

      travelData.calendar = new Calendar(payload.time_period)
      if (process.env.NODE_ENV !== 'production') {
        console.log('payload.travel', payload.travel)
      }

      travelData.travel = new Travel(payload.travel)

      const rawCurrentDate = new Date(travelData.travel.datetimestart)
      travelData.currentDate = dateToCalendarEntry(rawCurrentDate)
      travelData.currentUser = travelData.travel.user

      const userTravels = travelData.calendar.getTravelsByUser(travelData.currentDate, travelData.currentUser.userId)
      travelData.currentCalendarTravel = userTravels.length > 0 ? userTravels[userTravels.length - 1] : new CalendarTravelEntry()

      travelData.driversStatistics = Object.assign({}, ...Object.keys(payload.drivers_statistics).map(key => ({
        [key]: new UserStatistics(payload.drivers_statistics[key])
      })))
    },
    [CRITICAL_EVENT_STATE_UPDATING] (state, payload) {
      let travelData = getTravelData(state)
      if (payload.index >= 0 && payload.index < travelData.travel.criticalEvents.length) {
        const criticalEvent = travelData.travel.criticalEvents[payload.index]
        if (criticalEvent) {
          criticalEvent.state = payload.request.state
        }
      }
    },
    [USER_FEEDBACK_UPDATING] (state, payload) {
      let travelData = getTravelData(state)
      travelData.travel.feedback = payload.map(entry => new UserFeedback(entry))
    },
    [TRAVEL_FETCHING] (state, payload) {
      let travelData = getTravelData(state)
      if (process.env.NODE_ENV !== 'production') {
        console.log('travel', payload)
      }
      travelData.travel = new Travel(payload)
    },
    [CALENDAR_FETCHING] (state, payload) {
      let travelData = getTravelData(state)
      travelData.calendar = new Calendar(payload)
    },
    [STATE_CLEARING] (state) {
      Object.assign(state, getDefaultState())
    },
    [LOAD_FLAG_SETTING] (state, payload) {
      state.isLoaded = payload.isLoaded
    },
    [TRAVEL_FLAG_LOADING] (state, payload) {
      let travelData = getTravelData(state)
      travelData.isTravelLoaded = payload.isTravelLoaded
    },
    [TRAVEL_SELECTION] (state, payload) {
      let travelData = getTravelData(state)
      travelData.isTravelSelected = payload.isTravelSelected
    },
    [CALENDAR_FLAG_LOADING] (state, payload) {
      let travelData = getTravelData(state)
      travelData.isCalendarLoaded = payload.isCalendarLoaded
    }
  },
  actions: {

    async [FETCH_DATA] (context, params = {}) {
      context.commit(LOAD_FLAG_SETTING, { isLoaded: false })
      context.commit(TRAVEL_FLAG_LOADING, { isTravelLoaded: false })

      try {
        const response = await $axios.get('map/', { params }) // { params } => {params: params}
        context.commit(DATA_FETCHING, response.data)

        // set user statistics
        context.dispatch(`${USER_STATISTICS_MODULE}/${USER_STATISTICS_UPDATE}`, response.data.user_statistics, { root: true })
      } catch (e) {
        console.error(e)
      }

      context.commit(CALENDAR_FLAG_LOADING, { isCalendarLoaded: true })
      context.commit(TRAVEL_FLAG_LOADING, { isTravelLoaded: true })
      context.commit(TRAVEL_SELECTION, { isTravelSelected: getTravelData(context.state).travel.travelId !== -1 })
      context.commit(LOAD_FLAG_SETTING, { isLoaded: true })
    },

    async [FETCH_TRAVEL] (context, params = { user: '', timestamp: Date.now().getTime() }) {
      context.commit(TRAVEL_FLAG_LOADING, { isTravelLoaded: false })
      context.commit(TRAVEL_SELECTION, { isTravelSelected: true })

      try {
        const response = await $axios.get('map/travel', { params })
        context.commit(TRAVEL_FETCHING, response.data)
      } catch (e) {
        console.error(e)
      }

      context.commit(TRAVEL_FLAG_LOADING, { isTravelLoaded: true })
    },

    async [FETCH_CALENDAR] (context, params = { year: null, month: null, user: null }) {
      context.commit(CALENDAR_FLAG_LOADING, { isCalendarLoaded: false })

      try {
        const response = await $axios.get('map/calendar', { params })
        context.commit(CALENDAR_FETCHING, response.data)
      } catch (e) {
        console.error(e)
      }

      context.commit(CALENDAR_FLAG_LOADING, { isCalendarLoaded: true })
    },

    async [UPDATE_CRITICAL_EVENT_STATE] (context, data = {
      request: {
        state: UserCriticalEventConfirmation.UNKNOWN,
        idCriticalEvent: -1
      },
      index: -1
    }) {
      try {
        const response = await $axios.post('map/critical_event_confirmation', data.request)
        if (response.data.success) {
          context.commit(CRITICAL_EVENT_STATE_UPDATING, data)
        } else {
          console.error(response.data)
        }
      } catch (e) {
        console.error(e)
      }
    },

    async [SEND_USER_FEEDBACK] (context, data = { feedback: [], trip_datetimestart: 0, userid: -1 }) {
      try {
        const response = await $axios.post('map/user_feedback', data)
        if (response.data.success) {
          context.commit(USER_FEEDBACK_UPDATING, data.feedback)
        } else {
          console.error(response.data)
        }
      } catch (e) {
        console.error(e)
      }
    },

    [CLEAR_MODULE_STATE] (context) {
      context.commit(STATE_CLEARING)
    }
  },
  getters: {
    getField,
    usernameFromDate: state => {
      const result = {}
      let travelData = getTravelData(state)

      if ('timePeriods' in travelData.calendar && travelData.currentDate in travelData.calendar.timePeriods) {
        for (const entry of travelData.calendar.timePeriods[travelData.currentDate]) {
          result[entry.user.userId] = entry.user.username
        }
      }

      return result
    },
    currentDateLocale: state => {
      let travelData = getTravelData(state)
      const parsed = travelData.currentDate.split('-')
      if (parsed.length === 3) {
        if ($vuetify.framework.lang.current === 'ru') {
          return `${parsed[2]}.${parsed[1]}.${parsed[0]}`
        } else if ($vuetify.framework.lang.current === 'en') {
          return `${parsed[0]}.${parsed[1]}.${parsed[2]}`
        } else {
          return travelData.currentDate
        }
      } else {
        return ''
      }
    }
  }
}
